import {inject, provide, reactive} from "vue";
import {IDefinition, IEvidenceMap, IGuideline, INode, IProt, IRct, ISr, ITopic} from "@/interfaces/evidence-map";
import {IMapState, IMapStore, ISearchResult} from "@/interfaces/mapStore";

export const useMapStore = (): IMapStore => inject<IMapStore>("mapStore")
export const createMapStore = (): IMapStore => {
    const state: IMapState = reactive<IMapState>({
        openStudyTypes: new Set<string>(),
        def: null,
        defGuide: false,
        guide: null,
        map: null,
        nodePath: [],
        prot: null,
        rct: null,
        sr: null,
        about: "",
        instructionsMap: "",
        instructionsTabular: "",
        defOpen: false,
        guideOpen: false,

    })
    const onDefClick = (def: IDefinition) => {
        clearStudy()
        state.def = def
    }
    const onGuideClick = (guide: IGuideline) => {
        clearStudy()
        state.guide = guide
    }
    const getParentFromRct = (rct: IRct): INode => {

        let node;
        state.map.nodes.forEach(n => {
            if (n.topic) {
                n.topic.rct?.forEach(r => {
                    if (rct === r) {
                        node = n
                    }
                })
            }
        })
        return node;
    }
    const getParentFromSr = (sr: ISr): INode => {
        let node;
        state.map.nodes.forEach(n => {
            if (n.topic) {
                n.topic.sr?.forEach(s => {
                    if (sr === s) {
                        node = n
                    }
                })
            }
        })
        return node;
    }
    const clearStudy = (): void => {
        state.prot = null
        state.rct = null
        state.sr = null
        state.def = null
        state.guide = null
    }
    const addToOpenStudyTypes = (id: string): void => {
        if (state.openStudyTypes.has(id)) {
            state.openStudyTypes.delete(id)
        } else {
            state.openStudyTypes.add(id)
        }
    }
    const searchPattern = (pattern: string): ISearchResult => {
        const p = pattern.toLowerCase();
        const result: ISearchResult = {
            rct: [],
            sr: [],
            nodes: [],
        };
        if (p.length < 2) {
            return result;
        }
        state.map.nodes.forEach(node => {
            if (node.title.toLowerCase().includes(p)) {
                result.nodes.push(node)
            }
            if (node.topic) {

                node.topic.rct?.forEach(rct => {
                    if (rct.title.toLowerCase().includes(p) || rct.firstAuthor.toLowerCase().includes(p)) {
                        result.rct.push(rct)
                    }
                })
                node.topic.sr?.forEach(sr => {
                    if (sr.title.toLowerCase().includes(p) || sr.firstAuthor.toLowerCase().includes(p)) {
                        result.sr.push(sr)
                    }
                })
            }
        })
        return result;
    }
    const getLevel = (node: INode, level: number): number => {

        if (node.parentId == 0) {
            return level
        } else {
            return getLevel(getNodeById(node.parentId), level + 1);
        }
    }
    const getProtById = (id: number): IProt => {
        let study = null;
        state.map.nodes.forEach(node => {
            node.topic?.prot?.forEach(prot => {
                if (prot.id == id) {
                    study = prot;
                }
            })
        })
        return study;
    }
    const getSrById = (id: number): ISr => {
        let study = null;
        state.map.nodes.forEach(node => {
            node.topic?.sr?.forEach(sr => {
                if (sr.id == id) {
                    study = sr;
                }
            })
        })
        return study;
    }
    const getRctById = (id: number): IRct => {
        let study = null;
        state.map.nodes.forEach(node => {
            node.topic?.rct?.forEach(rct => {
                if (rct.id == id) {
                    study = rct;
                }
            })
        })
        return study;
    }
    const getNodeById = (id: number) => {
        return state.map.nodes.find(x => x.id == id);
    }
    const getNodesByParent = (id: number) => {
        return state.map.nodes.filter(x => x.parentId == id);
    }

    const addToNodePath = (node: INode) => {
        state.defGuide = false
        state.guideOpen = false
        state.defOpen = false
        state.nodePath.length = 0
        if (node.parentId != 0) {
            state.nodePath.push(...getParents(node, []).reverse())
        }
        state.nodePath.push(node);
    }
    const getParents = (node: INode, parents: INode[]): INode[] => {
        if (node.parentId != 0) {
            const parent = getNodeById(node.parentId)
            parents.push(parent)
            return getParents(parent, parents)
        }
        return parents
    }
    const onNodeClick = (a_Node: INode) => {
        clearStudy()
        addToNodePath(a_Node);
        window.dispatchEvent(new Event("redraw"));
    }
    const onSrClick = (a_Sr: ISr) => {
        clearStudy()
        state.sr = a_Sr;
        addToNodePath(getParentFromSr(a_Sr))
        window.dispatchEvent(new Event("redraw"));
    }
    const onRctClick = (a_Rct: IRct) => {
        clearStudy()
        state.rct = a_Rct;
        addToNodePath(getParentFromRct(a_Rct))
        window.dispatchEvent(new Event("redraw"));
    }
    const goToDefGuide = () => {
        state.defGuide = true
        state.nodePath.length = 0
    }
    const getDefById = (a_Id: number) => {
        return state.map.definitionsAndGuidelines.definitions.find(x => x.id == a_Id)
    }
    const getGuideById = (a_Id: number) => {
        return state.map.definitionsAndGuidelines.guidelines.find(x => x.id == a_Id)
    }
    const onGuideOpen = () => {
        state.guideOpen = !state.guideOpen
    }
    const onDefOpen = () => {
        state.defOpen = !state.defOpen
    }
    const reset = () => {
        clearStudy()
        state.defGuide = false
        state.defOpen = false
        state.guideOpen = false
        state.nodePath.length = 0
        window.dispatchEvent(new Event("redraw"));
    }
    return {
        onDefOpen,
        onGuideOpen,
        state,
        getDefById,
        getGuideById,
        addToNodePath,
        addToOpenStudyTypes,
        clearStudy,
        getLevel,
        getNodeById,
        getNodesByParent,
        getParentFromRct,
        getParentFromSr,
        getParents,
        getProtById,
        getRctById,
        getSrById,
        goToDefGuide,
        onDefClick,
        onGuideClick,
        onNodeClick,
        onRctClick,
        onSrClick,
        reset,
        searchPattern
    }


}

export const fetchMap = async (a_Url?: string): Promise<IEvidenceMap> => {
    const l_Url = a_Url ? a_Url : "https://eviglance.com/data/46gPnYJ456247S5Mdm4W/EMPS/EMPS.json";
    const l_Res = await fetch(
        l_Url,
        {
            mode: "cors"
        }
    )
    return await l_Res.json();
}
export const fetchEviglance = async (): Promise<any> => {
    const l_Res = await fetch(
        "https://eviglance.com/data/46gPnYJ456247S5Mdm4W/EVIglance.json",

        {
            mode: "cors"
        })
    return await l_Res.json()
}
